import { FlagsProvider } from '@atlaskit/flag';
import { xcss, Box } from '@atlaskit/primitives';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input';
import {
  closeInlineRovoPrompt,
  inlineRovoPromptState,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { useLocale } from '@databases/intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IntlProvider } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { currentOrgData } from '@canvas/canvas-state/org-data';

import { useInlineRovoPromptPopper } from './hooks/useInlineRovoPromptPopper';

const PRODUCT_KEY = 'confluence';
const SOURCE = 'CONFLUENCE_INLINE_EDITOR';

const promptStyles = xcss({
  width: '400px',
  zIndex: 'modal',
  opacity: 0,
  transition: 'opacity 100ms ease-in-out',
});

const openPromptStyles = xcss({
  opacity: 1,
});

const fallbackNoReferenceElementPromptStyles = xcss({
  position: 'fixed',
  bottom: '150px',
  left: '50%',
  transform: 'translate(-50%)',
});

interface InlineRovoPromptProps {
  container: HTMLElement;
}

export const InlineRovoPrompt = (props: InlineRovoPromptProps) => {
  const { isOpen } = useSnapshot(inlineRovoPromptState);

  if (!isOpen) {
    return null;
  }

  return <InlineRovoPromptInternal {...props} />;
};

const InlineRovoPromptInternal = ({ container }: InlineRovoPromptProps) => {
  const locale = useLocale();
  const cloudId = getCloudId();
  const { contentId } = useDatabaseContent();
  const orgData = useSnapshot(currentOrgData);
  const popperRef = useRef<HTMLDivElement>(null);
  const [fadeIn, setFadeIn] = useState(false);

  const { attributes, styles, referenceElement } = useInlineRovoPromptPopper(
    popperRef.current,
    container
  );

  // Trigger fade in after mount, to allow style transitions to play
  useEffect(() => {
    setFadeIn(true);
  }, []);

  const handleAbort = useCallback(() => {
    closeInlineRovoPrompt();
  }, []);

  const handleComplete = useCallback(() => {
    closeInlineRovoPrompt();
  }, []);

  // Don't allow mouse and keyboard inputs to bubble up beyond the inline prompt.
  // This prevents unwanted side effects to the database while interacting with the prompt (e.g. changing selection, modifying values, etc)
  const stopEventPropagation = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      event.stopPropagation();
    },
    []
  );

  const handleKeyDownCapture = useCallback((event: React.KeyboardEvent) => {
    // Stop propagation for Enter key during capture phase.
    // This prevents the standard DBs keyboard logic from handling Enter key presses meant for the prompt.
    if (event.key === 'Enter') {
      event.stopPropagation();
    }
  }, []);

  // Apply popper styles and attributes if we have a reference element.
  // Otherwise, we'll fall back to fixed positioning styles.
  const popperStyles = referenceElement ? styles.popper : undefined;
  const popperAttributes = referenceElement ? attributes.popper : undefined;

  return createPortal(
    <IntlProvider locale={locale}>
      <FlagsProvider>
        <Box
          xcss={[
            promptStyles,
            fadeIn && openPromptStyles,
            !referenceElement && fallbackNoReferenceElementPromptStyles,
          ]}
          {...popperAttributes}
          testId="inline-rovo-chat-prompt"
          onKeyDown={stopEventPropagation}
          onKeyDownCapture={handleKeyDownCapture}
          onMouseDown={stopEventPropagation}
          ref={popperRef}
          style={popperStyles}
        >
          <RovoInlineChatPrompt
            source={SOURCE}
            productKey={PRODUCT_KEY}
            siteId={cloudId || ''}
            orgId={orgData.id || ''}
            objectId={contentId}
            onAbort={handleAbort}
            onComplete={handleComplete}
            autoClose={true}
          />
        </Box>
      </FlagsProvider>
    </IntlProvider>,
    document.body
  );
};
